import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  Renderer2,
  TemplateRef,
  ViewContainerRef,
  OnDestroy,
  EmbeddedViewRef,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { EntityState, IEntityState } from '../../_models/IChangedEntity';

export interface TooltipContext {
  // État
  state: EntityState;
  stateLabel: string;
  stateColor: string;
  stateIcon: string;

  // Données entité
  entity: any;
  modifiedProperties: string[];

  // Actions
  dismiss: () => void;
}


export interface TooltipEntityContext {
  entity: IEntityState; 
  dismiss: () => void;
}

@Directive({
  selector: '[appRichTooltip]',
  exportAs: 'appRichTooltip',
  standalone: true
})
export class RichTooltipDirective implements OnDestroy, OnChanges {
  @Input('appRichTooltip') template: TemplateRef<any> | null = null;
  @Input() tooltipContext: TooltipEntityContext | null = null;
  @Input() tooltipPosition: 'top' | 'bottom' | 'left' | 'right' = 'top';
  @Input() tooltipAlignment: 'start' | 'center' | 'end' = 'center';
  @Input() tooltipDelay: number = 0;
  @Input() tooltipHideDelay: number = 100;

  private overlay: HTMLElement | null = null;
  private embeddedView: EmbeddedViewRef<any> | null = null;
  private isVisible = false;
  private showTimeout: any = null;
  private hideTimeout: any = null;
  private isMouseOverTooltip = false;
  private shouldShow: boolean = false;

  private readonly stateConfig: Record<EntityState, { label: string; color: string; icon: string }> = {
    [EntityState.Detached]: { label: 'Detached', color: 'gray', icon: '○' },
    [EntityState.Unchanged]: { label: 'Unchanges', color: 'gray', icon: '○' },
    [EntityState.Deleted]: { label: 'Deleted', color: 'red', icon: '✕' },
    [EntityState.Modified]: { label: 'Modified', color: 'amber', icon: '✎' },
    [EntityState.Added]: { label: 'Created', color: 'emerald', icon: '✚' }
  };

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private vcr: ViewContainerRef
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tooltipContext']) {
      this.evaluateShouldShow();
    }
  }

  private evaluateShouldShow(): void {
    if (!this.tooltipContext) {
      this.shouldShow = false;
      return;
    }
    const state = this.tooltipContext.entity._entityState;
    this.shouldShow = state === EntityState.Deleted ||
      state === EntityState.Modified ||
      state === EntityState.Added;
  }

  private buildContext(): any  {
    if (!this.tooltipContext) return null;

  const entity = this.tooltipContext.entity;
  const state = entity._entityState;
  const config = this.stateConfig[state];

    return {
      ...this.tooltipContext,  // contient entity et dismiss
      state,
      stateLabel: config.label,
      stateColor: config.color,
      stateIcon: config.icon,
      modifiedProperties: Array.from(entity._modifiedProperties || []),
      dismiss: this.tooltipContext.dismiss || (() => { })
    };
  }

  @HostListener('mouseenter')
  onMouseEnter() {
    this.evaluateShouldShow();
    if (!this.shouldShow) return;

    this.cancelHide();
    if (!this.isVisible) {
      this.scheduleShow();
    }
  }

  @HostListener('mouseleave')
  onMouseLeave() {
    this.cancelShow();
    setTimeout(() => {
      if (!this.isMouseOverTooltip) {
        this.scheduleHide();
      }
    }, 10);
  }

  private scheduleShow() {
    this.showTimeout = setTimeout(() => {
      this.show();
    }, this.tooltipDelay);
  }

  private cancelShow() {
    if (this.showTimeout) {
      clearTimeout(this.showTimeout);
      this.showTimeout = null;
    }
  }

  private scheduleHide() {
    this.hideTimeout = setTimeout(() => {
      this.hide();
    }, this.tooltipHideDelay);
  }

  private cancelHide() {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
  }

  private show() {
    if (!this.shouldShow || !this.template || this.isVisible) return;

    // Créer l'overlay
    this.overlay = this.renderer.createElement('div');

    const containerClasses = [
      'absolute', 'z-50', 'opacity-0',
      'transition-all', 'duration-300', 'scale-95'
    ];

    containerClasses.forEach(cls => {
      this.renderer.addClass(this.overlay, cls);
    });

    // Événements sur le tooltip
    this.renderer.listen(this.overlay, 'mouseenter', () => {
      this.isMouseOverTooltip = true;
      this.cancelHide();
    });

    this.renderer.listen(this.overlay, 'mouseleave', () => {
      this.isMouseOverTooltip = false;
      this.scheduleHide();
    });

    // Créer la vue à partir du template avec le contexte
    const context = this.buildContext();
    this.embeddedView = this.vcr.createEmbeddedView(this.template, {
      $implicit: context,
      ...context
    });

    // Ajouter le DOM du template à l'overlay
    this.embeddedView.rootNodes.forEach(node => {
      this.renderer.appendChild(this.overlay, node);
    });

    this.renderer.appendChild(document.body, this.overlay);

    this.positionTooltip();

    requestAnimationFrame(() => {
      if (this.overlay) {
        this.renderer.removeClass(this.overlay, 'opacity-0');
        this.renderer.removeClass(this.overlay, 'scale-95');
        this.renderer.addClass(this.overlay, 'opacity-100');
        this.renderer.addClass(this.overlay, 'scale-100');
      }
    });

    this.isVisible = true;
  }

  private positionTooltip(): void {
    if (!this.overlay) return;

    const hostRect = this.el.nativeElement.getBoundingClientRect();
    const rect = this.overlay.getBoundingClientRect();

    let top = hostRect.top - rect.height - 12;
    let left: number;

    switch (this.tooltipAlignment) {
      case 'start':
        left = hostRect.left;
        break;
      case 'end':
        left = hostRect.right - rect.width;
        break;
      case 'center':
      default:
        left = hostRect.left + (hostRect.width / 2) - (rect.width / 2);
        break;
    }

    if (left < 10) left = 10;
    if (left + rect.width > window.innerWidth - 10) {
      left = window.innerWidth - rect.width - 10;
    }

    this.renderer.setStyle(this.overlay, 'top', `${top + window.scrollY}px`);
    this.renderer.setStyle(this.overlay, 'left', `${left + window.scrollX}px`);
  }

  hide(immediate: boolean = false): void {
    this.isMouseOverTooltip = false;
    this.cancelHide();
    this.cancelShow();

    if (immediate) {
      // Fermeture immédiate sans animation
      if (this.overlay) {
        this.renderer.removeChild(document.body, this.overlay);
        this.overlay = null;
      }
      if (this.embeddedView) {
        this.embeddedView.destroy();
        this.embeddedView = null;
      }
      this.isVisible = false;
    } else {
      // Fermeture avec animation (comportement normal)
      if (this.overlay) {
        this.renderer.removeClass(this.overlay, 'opacity-100');
        this.renderer.removeClass(this.overlay, 'scale-100');
        this.renderer.addClass(this.overlay, 'opacity-0');
        this.renderer.addClass(this.overlay, 'scale-95');
        setTimeout(() => {
          if (this.overlay) {
            this.renderer.removeChild(document.body, this.overlay);
            this.overlay = null;
          }
          if (this.embeddedView) {
            this.embeddedView.destroy();
            this.embeddedView = null;
          }
          this.isVisible = false;
        }, this.tooltipHideDelay);
      }
    }
  }

  ngOnDestroy(): void {
    this.cancelShow();
    this.cancelHide();
    this.hide();
  }
}
